import { type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import {
  OSS_STATUS_LABEL,
  OSS_STATUS_TONE,
  type OssVerificationRequest,
} from '../lib/ossVerification';
import { absolute } from '../lib/time';
import { StatusPill } from './StatusPill';
import { Button } from './ui/Button';

// An OSS-verification application the account has already filed (arc 101 slice
// 07, #68). Presenter — pill, what was submitted, and the one line that says
// what happens next: pending waits on a human verdict, rejected shows the
// reviewer's reason (nothing else tells the applicant why), approved confirms.
// The parent owns the re-apply switch; this only asks for it.

export function OssVerifyStatus(props: {
  readonly request: OssVerificationRequest;
  /** Show the re-apply control — false for an approved row or a read-only caller. */
  readonly canEdit: boolean;
  readonly onEdit: () => void;
}): JSX.Element {
  return (
    <div class="oss-verify__status">
      <div class="oss-verify__head">
        <StatusPill tone={OSS_STATUS_TONE[props.request.status]}>
          {OSS_STATUS_LABEL[props.request.status]()}
        </StatusPill>
        <span class="oss-verify__repo">{props.request.repo_full_name}</span>
      </div>

      <p class="oss-verify__submitted">
        {t('oss_verify.submitted_at', { when: absolute(props.request.submitted_at_iso) })}
      </p>
      <p class="oss-verify__evidence">{props.request.evidence}</p>

      <Show when={props.request.status === 'pending'}>
        <p class="oss-verify__note">{t('oss_verify.pending_note')}</p>
      </Show>
      <Show when={props.request.status === 'approved'}>
        <p class="oss-verify__note">{t('oss_verify.approved_note')}</p>
      </Show>
      <Show when={props.request.status === 'rejected'}>
        <p class="oss-verify__note">
          {props.request.reviewer_note ?? t('oss_verify.rejected_no_note')}
        </p>
      </Show>

      <Show when={props.canEdit}>
        <div class="oss-verify__actions">
          <Button variant="outline" size="sm" onClick={props.onEdit}>
            {props.request.status === 'pending'
              ? t('oss_verify.edit')
              : t('oss_verify.apply_again')}
          </Button>
        </div>
      </Show>
    </div>
  );
}
